import React, { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import { poolLabel, relativeDeltaPercent, venueLabel } from 'utils/swap/rank'
import type { Quote, RouteCandidate, SwapToken } from 'utils/swap/types'

const List = styled(Flex)`
  flex-direction: column;
  gap: 6px;
  width: 100%;
`

const Row = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 10px 12px;
  border: 1px solid ${({ theme, selected }) => (selected ? theme.colors.cyan : theme.colors.gray8)};
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.gray10};
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  text-align: left;

  &:hover,
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const Left = styled(Flex)`
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const MoreButton = styled.button`
  min-height: 44px;
  padding: 4px 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.cyan};
  cursor: pointer;
  font-size: 12px;
  text-align: left;
`

/**
 * How many routes are shown before the list is asked for in full.
 *
 * ETH to USDC on Base discovers forty-seven of them, and a panel that lists
 * every one is a wall a person scrolls past rather than reads - most of the
 * tail is dust in pools nobody would choose. The tail is still there, one
 * press away, because a route we found and did not show would make the ranking
 * unauditable.
 */
export const ROUTES_SHOWN_BY_DEFAULT = 5

/**
 * A route in words: which venue, which pool, and whether it goes through
 * anything on the way. It is the difference between "we found you a price" and
 * "here is where the price comes from", and the second is the only one a
 * person can check.
 */
export function describeRoute(quote: Quote, hubSymbol?: string): string {
  if (quote.candidate.venue === 'kyber') return venueLabel('kyber')
  const candidate = quote.candidate as RouteCandidate
  const pools = candidate.pools
    .map((pool) => poolLabel(candidate.venue, pool))
    .filter(Boolean)
    .join(' + ')
  const base = pools ? `${venueLabel(candidate.venue)} ${pools}` : venueLabel(candidate.venue)
  if (candidate.path.length <= 2) return base
  return hubSymbol ? `${base} · via ${hubSymbol}` : `${base} · 2 hops`
}

interface RouteListProps {
  quotes: Quote[]
  selected: Quote | null
  tokenOut: SwapToken
  /** Resolves a hub address to its symbol, so a two-hop route can name it. */
  symbolFor: (address: string) => string | undefined
  onSelect: (quote: Quote) => void
}

const RouteList: React.FC<RouteListProps> = ({
  quotes,
  selected,
  tokenOut,
  symbolFor,
  onSelect
}) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const [showAll, setShowAll] = useState(false)
  if (quotes.length === 0) return null
  const best = quotes[0]
  const visible = showAll ? quotes : quotes.slice(0, ROUTES_SHOWN_BY_DEFAULT)

  return (
    <List>
      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {t('swap.routes')}
      </Text>
      {visible.map((quote, index) => {
        const isSelected = selected === quote
        const delta = relativeDeltaPercent(quote, best)
        const hub =
          quote.candidate.venue !== 'kyber' && (quote.candidate as RouteCandidate).path.length > 2
            ? symbolFor((quote.candidate as RouteCandidate).path[1])
            : undefined
        return (
          <Row
            // The venue and its pools identify a route; the index keeps two
            // identical-looking rows on different pools apart.
            key={`${quote.candidate.venue}-${index}`}
            type="button"
            selected={isSelected}
            onClick={() => onSelect(quote)}
          >
            <Left>
              <Text variant={TextVariant.BODY_SMALL_BOLD}>{describeRoute(quote, hub)}</Text>
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {Number(
                  ethers.utils.formatUnits(quote.amountOut.toString(), tokenOut.decimals)
                ).toLocaleString(undefined, { maximumFractionDigits: 6 })}{' '}
                {tokenOut.symbol}
              </Text>
            </Left>
            <Text
              variant={TextVariant.TEXT_XS_REGULAR}
              color={index === 0 ? colors.green : colors.gray6}
            >
              {index === 0 ? t('swap.bestRate') : `${delta.toFixed(2)} %`}
            </Text>
          </Row>
        )
      })}
      {quotes.length > ROUTES_SHOWN_BY_DEFAULT ? (
        <MoreButton type="button" onClick={() => setShowAll((v) => !v)}>
          {showAll
            ? t('swap.showFewerRoutes')
            : t('swap.showAllRoutes', { count: quotes.length })}
        </MoreButton>
      ) : null}
    </List>
  )
}

export default RouteList
